import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  Sparkles,
  Target,
  Heart,
  Award,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1400",
  team: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1200",
  mission: "https://images.unsplash.com/photo-1603201667141-5a2d4c673378?w=1200",
};

export default function AboutPage() {
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${IMG.hero})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-hero-gradient" aria-hidden />
        <div className="container-page relative z-10 py-24 text-center sm:py-32">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-brand-300">
            <Sparkles className="h-3.5 w-3.5" />
            About SnapPay
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            Pioneering Secure Payment Solutions for High-Risk Merchants
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            We specialize in providing cutting-edge payment solutions tailored specifically for high-risk merchants — combining deep industry expertise with modern financial technology.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid items-center gap-14 lg:grid-cols-2">
          <div>
            <span className="eyebrow">Our Story</span>
            <h2 className="section-title mt-3">Rewriting financial services one API at a time</h2>
            <p className="section-lead">
              Since our founding in 2015, SnapPay has been on a mission to create the most accessible financial services ecosystem in history. We are building the global operating system for fintech, starting with payments.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink-500">
              Moving money is a fundamental human activity and critical to the health of our global economy. However, innovation from established financial services providers has failed to keep pace with the needs of innovative SaaS platforms and their users. SnapPay exists to lower barriers to entry, drive competition, and make the financial system more equitable and accessible for people around the world.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink-500">
              Our team of experts is dedicated to staying ahead of the curve in the ever-evolving payment processing landscape. By leveraging the latest advancements in encryption, fraud detection, and risk management techniques, we offer our clients peace of mind, knowing their transactions are protected against potential threats.
            </p>
          </div>
          <img
            src={IMG.mission}
            alt="Our story"
            className="h-[480px] w-full rounded-3xl object-cover shadow-card"
            loading="lazy"
          />
        </div>
      </section>

      <section className="section bg-brand-50/60">
        <div className="container-page">
          <div className="text-center">
            <span className="eyebrow">Values</span>
            <h2 className="section-title mx-auto mt-3 max-w-3xl">
              What drives us every day
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Target,
                title: "Mission-Driven",
                body: "Every decision ties back to making payments more accessible for merchants everywhere.",
              },
              {
                icon: ShieldCheck,
                title: "Security First",
                body: "PCI DSS Level 1 certified with bank-grade encryption and fraud protection.",
              },
              {
                icon: Heart,
                title: "Customer Obsessed",
                body: "24/7 dedicated support from real payments experts who know your industry.",
              },
              {
                icon: Award,
                title: "Integrity",
                body: "Transparent pricing, honest advice, and relationships built to last.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-soft">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-ink-900">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-500">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid items-center gap-14 lg:grid-cols-2">
          <img
            src={IMG.team}
            alt="Our global team"
            className="h-[460px] w-full rounded-3xl object-cover shadow-card"
            loading="lazy"
          />
          <div>
            <span className="eyebrow">Our Beliefs</span>
            <h2 className="section-title mt-3">
              Every business deserves access to the best payment technology
            </h2>
            <p className="section-lead">
              We believe every business should have access to the best and most innovative payment technology — and that they should earn the bulk of the economic benefit. To accelerate access to payments and fintech for the greater good, we remove the technical, compliance, regulatory, and global barriers to entry.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 border-t border-ink-900/5 pt-8">
              <Stat value="$1.2B+" label="Monthly volume" />
              <Stat value="99.9%" label="Uptime" />
              <Stat value="20+" label="Acquiring partners" />
              <Stat value="150+" label="Currencies" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500 text-white">
        <div className="container-page grid items-center gap-8 py-16 md:grid-cols-[1.3fr_auto] md:py-20">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em]">
              <Users className="h-3.5 w-3.5" /> Join our network
            </div>
            <h2 className="font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
              Ready to build the future of payments together?
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact-us" className="btn-dark">
              Get in touch
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-ink-900 sm:text-3xl">
        {value}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-ink-500">
        {label}
      </div>
    </div>
  );
}
